"use client";

import Image from "next/image";
import Link from "next/link";
import { HiArrowRight } from "react-icons/hi2";
import { HomeSectionHeader } from "@/components/home/HomeSectionHeader";
import { Stagger, StaggerItem } from "@/components/motion/Stagger";
import { LinkButton } from "@/components/ui/LinkButton";
import type { HomeMiniGalleryContent } from "@/lib/api-types";
import { DEFAULT_HOME_MINI_GALLERY } from "@/lib/site-content-fallbacks";
import { cn } from "@/lib/utils";

const BENTO_LAYOUT = [
  "col-span-2 row-span-2 min-h-[220px] md:min-h-[320px]",
  "min-h-[120px] md:min-h-[150px]",
  "min-h-[120px] md:min-h-[150px]",
  "min-h-[120px] md:min-h-[150px]",
  "min-h-[120px] md:min-h-[150px]",
] as const;

function GalleryTile({
  src,
  alt,
  href,
  className,
  featured = false,
}: {
  src: string;
  alt: string;
  href: string;
  className?: string;
  featured?: boolean;
}) {
  return (
    <Link
      href={href}
      className={cn(
        "charity-gallery-tile group relative block h-full overflow-hidden rounded-2xl",
        "border border-[var(--charity-stone)] bg-[var(--card)] shadow-sm",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--charity-green)] focus-visible:ring-offset-2",
        className
      )}
    >
      <Image
        src={src}
        alt={alt}
        fill
        className="object-cover transition-transform duration-500 group-hover:scale-105"
        sizes="(max-width: 768px) 50vw, 25vw"
      />
      <div
        className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-transparent"
        aria-hidden
      />
      <div className="absolute inset-x-0 bottom-0 z-10 flex items-center justify-between p-4 text-white opacity-70 transition-opacity duration-300 [@media(hover:hover)]:opacity-0 [@media(hover:hover)]:group-hover:opacity-100 [@media(hover:hover)]:group-focus-within:opacity-100">
        <span className="text-sm font-medium">
          {featured ? "Featured moment" : "View impact"}
        </span>
        <HiArrowRight className="h-4 w-4" aria-hidden />
      </div>
    </Link>
  );
}

type HomeMiniGalleryProps = {
  gallery?: HomeMiniGalleryContent;
};

export function HomeMiniGallery({
  gallery = DEFAULT_HOME_MINI_GALLERY,
}: HomeMiniGalleryProps) {
  const photos = gallery.images.slice(0, 5);

  return (
    <section className="home-charity-section bg-[var(--charity-ivory)] py-16 md:py-20">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <HomeSectionHeader
            align="left"
            eyebrow={gallery.eyebrow}
            title={gallery.title}
            subtitle={gallery.subtitle}
            className="mb-0 flex-1 text-left"
          />
          <LinkButton
            href={gallery.ctaHref}
            variant="outline"
            size="md"
            className="shrink-0"
          >
            {gallery.ctaLabel}
          </LinkButton>
        </div>

        <Stagger className="grid grid-cols-2 gap-3 md:grid-cols-4 md:grid-rows-2 md:gap-4">
          {photos.map((photo, index) => (
            <StaggerItem
              key={photo.id}
              className={cn(BENTO_LAYOUT[index] ?? BENTO_LAYOUT[4], "h-full")}
            >
              <GalleryTile
                src={photo.src}
                alt={photo.alt}
                href={gallery.ctaHref}
                className="h-full w-full"
                featured={index === 0}
              />
            </StaggerItem>
          ))}
        </Stagger>
      </div>
    </section>
  );
}
